// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { ThemeProvider } from "next-themes";
import type { ReactNode } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/lib/auth";
import { DebugModeProvider } from "@/lib/debug-mode";
import { FeatureFlagProvider, mapFeatureFlags } from "@/lib/feature-flags";
import { routeTree } from "./routeTree.gen";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  scrollToTopSelectors: ["#main-scrollable-area"],
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function AuthBridge({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const flags = user ? mapFeatureFlags(user.featureFlags) : undefined;
  const isAdmin = user?.groups.includes("admin") ?? false;
  return (
    <DebugModeProvider isAdmin={isAdmin}>
      <FeatureFlagProvider flags={flags}>{children}</FeatureFlagProvider>
    </DebugModeProvider>
  );
}

export function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" storageKey="theme">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AuthBridge>
            <TooltipProvider>
              <RouterProvider router={router} />
            </TooltipProvider>
          </AuthBridge>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
